import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { OAuthService } from './oauth.service';
import { DebuggerService } from './debugger.service';
import { CACHEABLE } from '../interceptors/cache.interceptor';
import { isChunkLoadError } from '../utils/chunk-load-error';
import { AnalyticsDashboardDto } from 'src/app/Modules/analytics/analytics-dashboard.models';

/**
 * Event categories for organizing analytics
 */
export enum AnalyticsCategory {
  PAGE_VIEW = 'page_view',
  USER_ACTION = 'user_action',
  FEATURE_USAGE = 'feature_usage',
  NAVIGATION = 'navigation',
  FORM_INTERACTION = 'form_interaction',
  EXPORT = 'export',
  IMPORT = 'import',
  ERROR = 'error',
  PERFORMANCE = 'performance',
  AUTHENTICATION = 'authentication'
}

/**
 * Analytics event interface
 */
export interface AnalyticsEvent {
  // Core fields
  category: AnalyticsCategory;
  action: string;
  label?: string;
  value?: number;

  // Context fields
  module?: string;
  component?: string;

  // User context (auto-populated)
  userId?: string;
  userName?: string;
  userEmail?: string;
  userPermissions?: string[];

  // Session context (auto-populated)
  sessionId?: string;
  timestamp?: string;
  url?: string;
  userAgent?: string;
  screenResolution?: string;

  // Custom metadata
  metadata?: Record<string, any>;
}

export interface AnalyticsBreadcrumb {
  timestamp: string;
  type: 'app' | 'navigation' | 'http' | 'ui' | 'error' | 'debug';
  message: string;
  data?: Record<string, any>;
}

export interface AnalyticsBuildInfo {
  appVersion?: string;
  buildSha?: string;
  buildTime?: string;
  production?: boolean;
  apiRoot?: string;
}

/**
 * Backend analytics event model
 */
interface AnalyticsLogEntry {
  id?: number;
  category: string;
  action: string;
  label: string | null;
  value: number | null;
  module: string | null;
  component: string | null;
  userId: string;
  userName: string;
  userEmail: string;
  userPermissions: string;
  sessionId: string;
  timestamp: string;
  url: string;
  userAgent: string;
  screenResolution: string;
  metadata: string; // JSON stringified
  createdAt?: string;
}

/**
 * Resolved error pattern - stored in database to track fixed errors
 */
export interface ResolvedErrorPattern {
  id?: number;
  /** Unique key for this error type (`action::label`, truncated) */
  errorKey: string;
  /**
   * Server-computed, slash-free hash of `errorKey`. This is the canonical
   * identifier for the per-item unresolve DELETE — the raw `errorKey` cannot be
   * used as a URL path segment because HTTP-error keys embed the failing request
   * URL, whose slashes become `%2F` and make the server 404 the request.
   * Returned by GET/POST; the client never computes it.
   */
  keyHash?: string;
  /** The error action/type */
  action: string;
  /** First 100 chars of error label for display */
  labelPattern: string;
  /** Module where error occurred */
  module?: string;
  /** When it was marked as resolved */
  resolvedAt: string;
  /** User ID who resolved it */
  resolvedByUserId: string;
  /** User name who resolved it */
  resolvedByUserName: string;
  /** Optional note about the fix */
  note?: string;
}

/**
 * Analytics Service for tracking user interactions and application usage
 *
 * Features:
 * - Automatic user context enrichment
 * - Session tracking
 * - Offline queuing (localStorage fallback)
 * - Batch sending to reduce network calls
 * - Page view auto-tracking
 * - Performance monitoring helpers
 *
 * @example
 * // In a component
 * constructor(private analytics: AnalyticsService) {}
 *
 * ngOnInit() {
 *   this.analytics.trackPageView('roadmap-tracker', 'Roadmap Tracker');
 * }
 *
 * onExportPDF() {
 *   this.analytics.trackEvent({
 *     category: AnalyticsCategory.EXPORT,
 *     action: 'export_pdf',
 *     label: 'Roadmap Export',
 *     module: 'roadmap',
 *     component: 'roadmap-tracker'
 *   });
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private sessionId: string;
  private eventQueue: AnalyticsEvent[] = [];
  private batchSize = 10;
  private batchTimeout = 30000; // 30 seconds
  private batchTimer: any;
  /** Upper bound on retained events while the ingest endpoint is failing. */
  private readonly maxQueueSize = 500;
  /** Must not exceed the server's MaxBatchSize — it drops the overflow and still returns 200. */
  private readonly serverBatchLimit = 200;
  private readonly QUEUE_KEY = 'analytics_queue';
  private readonly SESSION_KEY = 'analytics_session';
  private enabled = environment.analyticsEnabled !== undefined ? environment.analyticsEnabled : true; // Controlled via environment

  // Cache user data to avoid async calls on every event
  private cachedUserId: string = 'anonymous';
  private cachedUserName: string = 'Unknown';
  private cachedUserEmail: string = '';
  private cachedUserPermissions: string[] = [];
  private previousRoute: string = '';

  private readonly breadcrumbs: AnalyticsBreadcrumb[] = [];
  private readonly maxBreadcrumbs = 50;
  private readonly maxBreadcrumbsInEvent = 25;
  // Non-error events only need a hint of what led up to them; the full trail is for diagnosing errors.
  private readonly maxBreadcrumbsInNonErrorEvent = 2;

  private lastGlobalErrorSignature: string | null = null;
  private lastGlobalErrorAtMs = 0;

  private readonly buildInfo: AnalyticsBuildInfo;

  constructor(
    private http: HttpClient,
    private oauthService: OAuthService,
    private debuggerService: DebuggerService,
    private router: Router
  ) {
    this.enabled = environment.analyticsEnabled !== undefined ? environment.analyticsEnabled : true;
    // React to debug mode changes
    this.debuggerService.debugMode$.subscribe((debugActive) => {
      this.enabled = environment.analyticsEnabled !== undefined ? environment.analyticsEnabled : true;
      if (debugActive) {
        this.enabled = false;
      }
    });
    this.sessionId = this.getOrCreateSessionId();
    this.buildInfo = this.resolveBuildInfo();
    this.loadQueuedEvents();
    this.setupBeforeUnload();
    this.initializeUserCache();

    // Keep lightweight breadcrumbs even when full navigation tracking is disabled.
    this.setupNavigationBreadcrumbs();
    this.setupGlobalErrorCapture();
    this.addBreadcrumb('app', 'app_start', {
      initialUrl: this.safeUrl(window.location.href),
      referrer: document.referrer || undefined,
      userAgent: navigator.userAgent,
      screen: `${window.screen.width}x${window.screen.height}`
    });
  }

  /**
   * Add a breadcrumb for richer error diagnosis.
   */
  addBreadcrumb(type: AnalyticsBreadcrumb['type'], message: string, data?: Record<string, any>): void {
    if (!this.enabled) return;

    try {
      this.breadcrumbs.push({
        timestamp: new Date().toISOString(),
        type,
        message,
        data
      });
      if (this.breadcrumbs.length > this.maxBreadcrumbs) {
        this.breadcrumbs.splice(0, this.breadcrumbs.length - this.maxBreadcrumbs);
      }
    } catch {
      // Never let telemetry break the app
    }
  }

  addHttpBreadcrumb(details: {
    phase: 'start' | 'success' | 'error';
    method?: string;
    url?: string;
    status?: number;
    durationMs?: number;
  }): void {
    this.addBreadcrumb('http', `http_${details.phase}`, {
      method: details.method,
      url: details.url ? this.safeUrl(details.url) : undefined,
      status: details.status,
      durationMs: details.durationMs
    });
  }

  /**
   * Initialize user data cache from OAuth service
   * Deferred to avoid TDZ errors from shareReplay(1) replaying cached values
   */
  private initializeUserCache(): void {
    // Defer subscription to next event loop tick to ensure all RxJS operators
    // are fully initialized before the shareReplay(1) replays its cached value.
    // This prevents "Cannot access before initialization" errors when loginChanged
    // immediately replays the last authentication state to new subscribers.
    setTimeout(() => {
      // Try to get initial user data
      this.oauthService.getUser()
        .then(user => {
          if (user && !user.expired) {
            this.cachedUserId = user.profile.sub || 'anonymous';
            this.cachedUserName = user.profile.name || 'Unknown';
            this.cachedUserEmail = user.profile.email || '';
            this.cachedUserPermissions = user.profile.Permission || [];
          }
        })
        .catch(() => {
          // Silently fail during auth flow - use defaults
        });

      // Subscribe to login changes to update cache
      // Uses try-catch to handle edge cases during auth callback
      try {
        this.oauthService.loginChanged.subscribe({
          next: () => {
            this.oauthService.getUser().then(user => {
              if (user && !user.expired) {
                this.cachedUserId = user.profile.sub || 'anonymous';
                this.cachedUserName = user.profile.name || 'Unknown';
                this.cachedUserEmail = user.profile.email || '';
                this.cachedUserPermissions = user.profile.Permission || [];
              } else {
                // User logged out - reset to defaults
                this.cachedUserId = 'anonymous';
                this.cachedUserName = 'Unknown';
                this.cachedUserEmail = '';
                this.cachedUserPermissions = [];
              }
            }).catch(() => {
              // Silently handle errors during logout
            });
          },
          error: (err) => {
            // Silently handle subscription errors during auth flow
            console.warn('Analytics: loginChanged subscription error', err);
          }
        });
      } catch (err) {
        // Catch any synchronous errors during subscription setup
        console.warn('Analytics: Failed to subscribe to loginChanged', err);
      }
    }, 0);
  }

  /**
   * Setup automatic navigation tracking
   */
  private setupNavigationTracking(): void {
    this.previousRoute = this.router.url;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const fromRoute = this.previousRoute;
      const toRoute = event.urlAfterRedirects;

      // Skip auth-related routes (login redirects, callbacks, etc.)
      if (this.isAuthRoute(fromRoute) || this.isAuthRoute(toRoute)) {
        // Still update previousRoute so we don't lose track
        this.previousRoute = toRoute;
        return;
      }

      // Skip if navigating to the same route (query param changes, etc.)
      if (this.getRoutePath(fromRoute) === this.getRoutePath(toRoute)) {
        this.previousRoute = toRoute;
        return;
      }

      // Track navigation
      this.trackNavigation(fromRoute, toRoute);

      // Update previous route
      this.previousRoute = toRoute;
    });
  }

  /**
   * Lightweight navigation breadcrumbs (no analytics events).
   */
  private setupNavigationBreadcrumbs(): void {
    this.previousRoute = this.router.url;

    this.router.events.pipe(
      filter(
        (event) =>
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
      )
    ).subscribe({
      next: (event) => {
        if (event instanceof NavigationStart) {
          this.addBreadcrumb('navigation', 'navigation_start', {
            from: this.safeUrl(this.previousRoute),
            to: this.safeUrl(event.url)
          });
        }
        if (event instanceof NavigationEnd) {
          this.addBreadcrumb('navigation', 'navigation_end', {
            from: this.safeUrl(this.previousRoute),
            to: this.safeUrl(event.urlAfterRedirects)
          });
          this.previousRoute = event.urlAfterRedirects;
        }
        if (event instanceof NavigationCancel) {
          this.addBreadcrumb('navigation', 'navigation_cancel', {
            url: this.safeUrl(event.url),
            reason: event.reason
          });
        }
        if (event instanceof NavigationError) {
          this.addBreadcrumb('navigation', 'navigation_error', {
            url: this.safeUrl(event.url),
            error: this.serializeUnknown(event.error)
          });
        }
      },
      error: () => {
        // ignore
      }
    });
  }

  private setupGlobalErrorCapture(): void {
    // Defer to avoid any bootstrap-time TDZ edge cases.
    setTimeout(() => {
      try {
        window.addEventListener('error', (event: ErrorEvent) => {
          // Stale-chunk failures are version skew, recovered by GlobalErrorHandler's
          // reload — not code errors.
          if (isChunkLoadError(event.error ?? event.message)) return;
          const normalized = this.normalizeError(event.error ?? event.message);
          if (this.shouldDedupeGlobalError(normalized.signature)) return;

          this.trackError(event.error ?? event.message, 'global', 'window', {
            source: 'window.error',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            ...normalized.details
          });
        });

        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
          if (isChunkLoadError(event.reason)) return;
          const normalized = this.normalizeError(event.reason);
          if (this.shouldDedupeGlobalError(normalized.signature)) return;

          this.trackError(event.reason, 'global', 'unhandledrejection', {
            source: 'window.unhandledrejection',
            ...normalized.details
          });
        });
      } catch {
        // ignore
      }
    }, 0);
  }

  private shouldDedupeGlobalError(signature: string): boolean {
    const now = Date.now();
    if (this.lastGlobalErrorSignature === signature && now - this.lastGlobalErrorAtMs < 1500) {
      return true;
    }
    this.lastGlobalErrorSignature = signature;
    this.lastGlobalErrorAtMs = now;
    return false;
  }

  /**
   * Check if route is auth-related (should not be tracked)
   */
  private isAuthRoute(route: string): boolean {
    if (!route) return true;

    const authPatterns = [
      '/signin-callback',
      '/signout-callback',
      '/silent-callback',
      '/signin-oidc',
      '/signout-oidc',
      '/login',
      '/logout',
      '/unauthorized',
      '/access-denied',
      'code=',  // OAuth callback with code parameter
      'state=', // OAuth state parameter
      'session_state=',
      'id_token=',
      'access_token='
    ];

    const lowerRoute = route.toLowerCase();
    return authPatterns.some(pattern => lowerRoute.includes(pattern));
  }

  /**
   * Extract path without query parameters for comparison
   */
  private getRoutePath(route: string): string {
    if (!route) return '';
    const queryIndex = route.indexOf('?');
    return queryIndex > -1 ? route.substring(0, queryIndex) : route;
  }

  /**
   * Track a custom analytics event
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.enabled) return;

    const enrichedEvent = this.enrichEvent(event);
    this.queueEvent(enrichedEvent);
  }

  /**
   * Track a page view
   */
  trackPageView(module: string, pageName: string, metadata?: Record<string, any>): void {
    this.trackEvent({
      category: AnalyticsCategory.PAGE_VIEW,
      action: 'view',
      label: pageName,
      module,
      metadata
    });
  }

  /**
   * Track a user action (button click, form submit, etc.)
   */
  trackAction(
    action: string,
    module: string,
    component: string,
    label?: string,
    metadata?: Record<string, any>
  ): void {
    this.trackEvent({
      category: AnalyticsCategory.USER_ACTION,
      action,
      label,
      module,
      component,
      metadata
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(
    featureName: string,
    module: string,
    metadata?: Record<string, any>
  ): void {
    this.trackEvent({
      category: AnalyticsCategory.FEATURE_USAGE,
      action: 'use_feature',
      label: featureName,
      module,
      metadata
    });
  }

  /**
   * Track export actions
   */
  trackExport(
    exportType: 'pdf' | 'json' | 'excel' | 'csv' | 'ics' | 'other',
    module: string,
    component: string,
    metadata?: Record<string, any>
  ): void {
    this.trackEvent({
      category: AnalyticsCategory.EXPORT,
      action: `export_${exportType}`,
      label: `${module}_${exportType}`,
      module,
      component,
      metadata
    });
  }

  /**
   * Track import actions
   */
  trackImport(
    importType: 'json' | 'excel' | 'csv' | 'other',
    module: string,
    component: string,
    success: boolean,
    metadata?: Record<string, any>
  ): void {
    this.trackEvent({
      category: AnalyticsCategory.IMPORT,
      action: `import_${importType}`,
      label: success ? 'success' : 'failure',
      module,
      component,
      value: success ? 1 : 0,
      metadata
    });
  }

  /**
   * Track navigation events
   */
  trackNavigation(fromRoute: string, toRoute: string): void {
    this.trackEvent({
      category: AnalyticsCategory.NAVIGATION,
      action: 'route_change',
      label: toRoute,
      metadata: {
        from: fromRoute,
        to: toRoute
      }
    });
  }

  /**
   * Track errors
   */
  trackError(
    errorMessage: unknown,
    module: string,
    component?: string,
    metadata?: Record<string, any>
  ): void {
    const normalized = this.normalizeError(errorMessage);
    const errorAction = this.getErrorAction(normalized.details);

    this.trackEvent({
      category: AnalyticsCategory.ERROR,
      action: errorAction,
      label: normalized.message,
      module,
      component,
      metadata: {
        ...metadata,
        ...normalized.details
      }
    });
  }

  private getErrorAction(details: Record<string, any> | undefined): string {
    if (!details) return 'error_occurred';

    if (details.errorType === 'NetworkUnavailable') {
      return 'network_unavailable';
    }

    if (details.errorType === 'HttpErrorResponse' && typeof details.httpStatus === 'number') {
      return `http_${details.httpStatus}`;
    }

    if (typeof details.errorType === 'string' && details.errorType.trim().length > 0) {
      return this.toActionKey(details.errorType);
    }

    return 'error_occurred';
  }

  /**
   * True when the request never produced a readable server response: status 0 (fetch
   * failed without the service worker), or the empty-body 504 that ngsw-worker's
   * safeFetch synthesizes whenever fetch() rejects — device offline / waking up, DNS,
   * or a cross-origin response without CORS headers. A real gateway 504 carries a body.
   */
  static isNetworkUnavailable(error: HttpErrorResponse): boolean {
    if (error.status === 0) return true;
    return error.status === 504 && (error.error === null || error.error === undefined || error.error === '');
  }

  private toActionKey(value: string): string {
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
  }

  /**
   * Track performance metrics
   */
  trackPerformance(
    metricName: string,
    durationMs: number,
    module: string,
    component?: string
  ): void {
    this.trackEvent({
      category: AnalyticsCategory.PERFORMANCE,
      action: metricName,
      value: durationMs,
      module,
      component,
      metadata: {
        duration_ms: durationMs
      }
    });
  }

  /**
   * Start a performance timer
   */
  startTimer(timerName: string): () => number {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
      return duration;
    };
  }

  /**
   * Enable or disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearQueue();
    }
  }

  /**
   * Flush all queued events immediately
   */
  flush(): Promise<void> {
    return this.sendBatch().toPromise().then(() => {});
  }

  /**
   * Get analytics statistics for admin dashboard (excludes error category)
   */
  getAnalytics(
    startDate?: string,
    endDate?: string,
    userId?: string,
    module?: string,
    limit: number = 20000,
    offset: number = 0
  ): Observable<any> {
    const params: any = { limit, offset };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (userId) params.userId = userId;
    if (module) params.module = module;

    return this.http.get(`${environment.apiRoot}analytics`, { params });
  }

  /**
   * Get the consolidated analytics dashboard payload — every aggregate the dashboard renders,
   * computed server-side over the full matching set. See AnalyticsDashboardDto.
   */
  getDashboard(startDate: string, endDate: string, bucket?: string): Observable<AnalyticsDashboardDto> {
    const params: any = { startDate, endDate };
    if (bucket) params.bucket = bucket;
    return this.http.get<AnalyticsDashboardDto>(`${environment.apiRoot}analytics/dashboard`, { params });
  }

  /**
   * Get error logs for the errors tab
   * @param includeResolved - if false, filter out errors matching resolved patterns
   */
  getErrors(
    startDate?: string,
    endDate?: string,
    includeResolved: boolean = false,
    limit: number = 500
  ): Observable<any> {
    const params: any = { limit, includeResolved };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    return this.http.get(`${environment.apiRoot}analytics/errors`, { params });
  }

  /**
   * Get all resolved error patterns
   */
  getResolvedErrors(): Observable<ResolvedErrorPattern[]> {
    return this.http.get<ResolvedErrorPattern[]>(`${environment.apiRoot}analytics/resolved-errors`);
  }

  /**
   * Mark an error pattern as resolved
   */
  resolveError(pattern: ResolvedErrorPattern): Observable<ResolvedErrorPattern> {
    return this.http.post<ResolvedErrorPattern>(`${environment.apiRoot}analytics/resolved-errors`, pattern);
  }

  /**
   * Mark every current error pattern as resolved. Errors that occur after this
   * operation remain visible as newly reappeared errors.
   */
  resolveAllCurrentErrors(): Observable<{ resolvedCount: number; resolvedAt: string }> {
    return this.http.post<{ resolvedCount: number; resolvedAt: string }>(
      `${environment.apiRoot}analytics/resolved-errors/resolve-all-current`,
      null
    );
  }

  /**
   * Unmark an error pattern as resolved (reopen).
   *
   * Pass the pattern's server-assigned `keyHash` (a slash-free hash of `errorKey`),
   * NOT the raw `errorKey`. HTTP-error keys embed the failing request URL, whose
   * slashes `encodeURIComponent`s to `%2F`; ASP.NET Core will not route a path
   * segment containing encoded slashes and 404s the DELETE. The hash is safe in a
   * path segment. (`encodeURIComponent` here is a harmless no-op for hex hashes.)
   */
  unresolveError(keyHash: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiRoot}analytics/resolved-errors/${encodeURIComponent(keyHash)}`);
  }

  /**
   * Clear all resolved error patterns
   */
  clearAllResolvedErrors(): Observable<void> {
    return this.http.delete<void>(`${environment.apiRoot}analytics/resolved-errors`);
  }

  /**
   * Enrich event with user and session context
   */
  private enrichEvent(event: AnalyticsEvent): AnalyticsEvent {
    const activated = this.isErrorEvent(event) ? this.getActivatedRouteInfo() : undefined;

    return {
      ...event,
      userId: this.cachedUserId,
      userName: this.cachedUserName,
      userEmail: this.cachedUserEmail,
      userPermissions: [...this.cachedUserPermissions],
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      // safeUrl, not the raw href: this field is the one attached to every event,
      // including errors thrown on /signin-callback, where the raw URL carries the
      // OAuth authorization code and session_state.
      url: this.safeUrl(window.location.href),
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      metadata: this.isErrorEvent(event)
        ? {
            ...event.metadata,
            build: this.buildInfo,
            route: {
              current: this.safeUrl(this.router.url),
              previous: this.safeUrl(this.previousRoute),
              activated
            },
            breadcrumbs: this.breadcrumbs.slice(-this.maxBreadcrumbsInEvent),
            jsBundles: this.extractBundleCluesFromStack(event.metadata?.stack)
          }
        : {
            ...event.metadata,
            breadcrumbs: this.breadcrumbs.slice(-this.maxBreadcrumbsInNonErrorEvent)
          }
    };
  }

  /**
   * Errors keep the full diagnostic context (build, route, 25 breadcrumbs, bundle clues);
   * every other event is stored far more often and gets only the slim form.
   */
  private isErrorEvent(event: AnalyticsEvent): boolean {
    return event.category === AnalyticsCategory.ERROR;
  }

  private resolveBuildInfo(): AnalyticsBuildInfo {
    const envAny = environment as any;
    return {
      appVersion: envAny.appVersion,
      buildSha: envAny.buildSha,
      buildTime: envAny.buildTime,
      production: environment.production,
      apiRoot: environment.apiRoot
    };
  }

  private normalizeError(error: unknown): { message: string; signature: string; details: Record<string, any> } {
    // HttpErrorResponse has useful structured fields.
    if (error instanceof HttpErrorResponse) {
      const msg = error.message || `HTTP ${error.status}`;
      const signature = `HttpErrorResponse:${error.status}:${error.url || ''}:${msg}`;
      const networkUnavailable = AnalyticsService.isNetworkUnavailable(error);
      return {
        message: msg,
        signature,
        details: {
          errorType: networkUnavailable ? 'NetworkUnavailable' : 'HttpErrorResponse',
          httpStatus: error.status,
          httpStatusText: error.statusText,
          requestUrl: error.url,
          serverMessage: this.extractServerMessage(error),
          stack: (error as any)?.stack,
          ...(networkUnavailable ? { navigatorOnLine: navigator.onLine } : {})
        }
      };
    }

    if (error instanceof Error) {
      const signature = `${error.name}:${error.message}:${(error.stack || '').split('\n')[0] || ''}`;
      return {
        message: error.message || error.toString(),
        signature,
        details: {
          errorType: error.name,
          stack: error.stack
        }
      };
    }

    // Some callers pass strings or random objects.
    const serialized = this.serializeUnknown(error);
    const message = typeof error === 'string' ? error : (serialized.message || 'Unknown error');
    const signature = `Unknown:${message}`;
    return {
      message,
      signature,
      details: {
        errorType: serialized.type || typeof error,
        stack: serialized.stack,
        raw: serialized.raw
      }
    };
  }

  private extractServerMessage(error: HttpErrorResponse): string | undefined {
    const err = error.error;
    if (!err) return undefined;
    if (typeof err === 'string') return err;
    if (err instanceof ErrorEvent) return err.message;
    if (typeof err === 'object') {
      return (err as any).message || (err as any).error || (err as any).title;
    }
    return undefined;
  }

  private serializeUnknown(error: unknown): { type?: string; message?: string; stack?: string; raw?: any } {
    try {
      if (error instanceof Error) {
        return { type: error.name, message: error.message, stack: error.stack };
      }
      if (typeof error === 'string') {
        return { type: 'string', message: error };
      }
      if (error && typeof error === 'object') {
        const anyErr = error as any;
        return {
          type: anyErr?.name || 'object',
          message: anyErr?.message,
          stack: anyErr?.stack,
          raw: this.safeJsonify(anyErr)
        };
      }
      return { type: typeof error, raw: error };
    } catch {
      return { type: typeof error };
    }
  }

  private safeJsonify(value: any): any {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return undefined;
    }
  }

  private safeUrl(url: string): string {
    // Prevent leaking OAuth params into analytics.
    try {
      const parsed = new URL(url, window.location.origin);
      const sameOrigin = parsed.origin === window.location.origin;
      const sensitive = ['code', 'state', 'session_state', 'id_token', 'access_token'];
      let hasSensitive = false;
      for (const key of sensitive) {
        if (parsed.searchParams.has(key)) {
          parsed.searchParams.delete(key);
          hasSensitive = true;
        }
      }
      // If we stripped sensitive data, also strip everything else for safety.
      if (hasSensitive) {
        parsed.search = '';
        parsed.hash = '';
      }
      // The AI assistant connect page's one-time values: dropped alone, the page's other params stay readable.
      for (const key of ['nonce', 'challenge']) {
        parsed.searchParams.delete(key);
      }
      const prefix = sameOrigin ? '' : `${parsed.host}`;
      return prefix + parsed.pathname + (parsed.search || '') + (parsed.hash || '');
    } catch {
      return url;
    }
  }

  private extractBundleCluesFromStack(stack: unknown): { files?: string[] } | undefined {
    if (!stack || typeof stack !== 'string') return undefined;
    // Matches Angular CLI output filenames like:
    // - main-XXXX.js
    // - runtime-XXXX.js
    // - polyfills-XXXX.js
    // - chunk-XXXX.js
    const matches = Array.from(
      stack.matchAll(
        /https?:\/\/[^\s)]+\/((?:main|polyfills|runtime|vendor|scripts)(?:-[^\s)\/]+)?\.js|chunk-[^\s)\/]+\.js)/g
      )
    );
    if (matches.length === 0) return undefined;
    const files = Array.from(new Set(matches.map((m) => m[1])));
    return { files };
  }

  private getActivatedRouteInfo(): { path?: string; component?: string; url?: string } {
    try {
      let route = this.router.routerState.snapshot.root;
      while (route.firstChild) {
        route = route.firstChild;
      }

      const routeConfigPath = route.routeConfig?.path;
      const componentName = (route.component as any)?.name;
      const url = route.url?.map((s) => s.path).join('/') || '';

      return {
        path: routeConfigPath,
        component: componentName,
        url: url ? `/${url}` : this.safeUrl(this.router.url)
      };
    } catch {
      return {};
    }
  }

  /**
   * Add event to queue and trigger batch send if needed
   */
  private queueEvent(event: AnalyticsEvent): void {
    this.eventQueue.push(event);

    // The backend now answers 503 (not 200) when a batch fails to persist, so failed events
    // stay queued and are retried instead of being silently discarded. Bound the queue so a
    // prolonged outage cannot grow it past the localStorage quota — drop the oldest events,
    // which are the least useful, and keep the most recent.
    if (this.eventQueue.length > this.maxQueueSize) {
      const dropped = this.eventQueue.length - this.maxQueueSize;
      this.eventQueue.splice(0, dropped);
      console.warn(`Analytics queue exceeded ${this.maxQueueSize} events; dropped the ${dropped} oldest.`);
    }

    this.saveQueueToStorage();

    // Send batch if queue reaches batch size
    if (this.eventQueue.length >= this.batchSize) {
      this.sendBatch().subscribe();
    } else {
      // Reset batch timer
      this.resetBatchTimer();
    }
  }

  /**
   * Send queued events to backend
   */
  private sendBatch(): Observable<any> {
    if (this.eventQueue.length === 0) {
      return of(null);
    }

    // Never send more than the server accepts in one call. It caps at 200 and silently drops
    // the overflow while still reporting success, so a larger batch would be acknowledged as
    // stored and then removed from the queue while most of it was thrown away.
    const batch = this.eventQueue.slice(0, this.serverBatchLimit);
    const logEntries: AnalyticsLogEntry[] = batch.map(e => this.mapToLogEntry(e));

    return this.http.post<{ success?: boolean }>(`${environment.apiRoot}analytics/batch`, logEntries, {
      context: new HttpContext().set(CACHEABLE, false)
    }).pipe(
      tap(response => {
        // A 200 carrying `success: false` is the pre-503 server telling us the write failed.
        // Treat it as a failure so the events are retried rather than dropped.
        if (response && response.success === false) {
          console.warn('Analytics batch rejected by server; keeping events queued for retry.');
          return;
        }

        // Remove sent events from queue
        this.eventQueue = this.eventQueue.slice(batch.length);
        this.saveQueueToStorage();
      }),
      catchError(error => {
        console.warn('Failed to send analytics batch:', error);
        // Keep events in queue for retry
        return of(null);
      })
    );
  }

  /**
   * Map analytics event to backend log entry format
   */
  private mapToLogEntry(event: AnalyticsEvent): AnalyticsLogEntry {
    return {
      category: event.category,
      action: event.action,
      label: event.label || null,
      value: event.value || null,
      module: event.module || null,
      component: event.component || null,
      userId: event.userId || '',
      userName: event.userName || '',
      userEmail: event.userEmail || '',
      userPermissions: (event.userPermissions || []).join(','),
      sessionId: event.sessionId || '',
      timestamp: event.timestamp || new Date().toISOString(),
      url: event.url || '',
      userAgent: event.userAgent || '',
      screenResolution: event.screenResolution || '',
      metadata: JSON.stringify(event.metadata || {})
    };
  }

  /**
   * Setup batch timer
   */
  private resetBatchTimer(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }
    this.batchTimer = setTimeout(() => {
      this.sendBatch().subscribe();
    }, this.batchTimeout);
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem(this.SESSION_KEY);
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem(this.SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save queue to localStorage for offline support
   */
  private saveQueueToStorage(): void {
    try {
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(this.eventQueue));
    } catch (e) {
      console.warn('Failed to save analytics queue to storage:', e);
    }
  }

  /**
   * Load queued events from localStorage
   */
  private loadQueuedEvents(): void {
    try {
      const stored = localStorage.getItem(this.QUEUE_KEY);
      if (stored) {
        this.eventQueue = JSON.parse(stored);
        // Try to send queued events — deferred out of the constructor on purpose.
        //
        // Sending here synchronously makes this the application's first HTTP request, which
        // forces Angular to build the HttpClient interceptor chain while this service (and
        // LoggerService, which depends on it) is still being constructed. Every interceptor
        // that injects LoggerService then receives Angular's internal CIRCULAR sentinel — an
        // empty object — because the cyclic-dependency guard is `ngDevMode`-only and stripped
        // from production builds. Interceptor instances are memoised for the lifetime of the
        // page, so those poisoned interceptors broke every subsequent POST/PUT/DELETE with
        // "this.logger.debug is not a function", and the queue was never drained — making the
        // state repeat on every reload.
        if (this.eventQueue.length > 0) {
          setTimeout(() => this.sendBatch().subscribe(), 0);
        }
      }
    } catch (e) {
      console.warn('Failed to load analytics queue from storage:', e);
    }
  }

  /**
   * Clear all queued events
   */
  private clearQueue(): void {
    this.eventQueue = [];
    localStorage.removeItem(this.QUEUE_KEY);
  }

  /**
   * Send remaining events before page unload
   */
  private setupBeforeUnload(): void {
    window.addEventListener('beforeunload', () => {
      if (this.eventQueue.length > 0) {
        // Use sendBeacon for reliable sending on page unload. Send the newest events — the
        // server caps the batch at serverBatchLimit and discards anything beyond it.
        const batch = this.eventQueue
          .slice(-this.serverBatchLimit)
          .map(e => this.mapToLogEntry(e));
        const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
        navigator.sendBeacon(`${environment.apiRoot}analytics/batch`, blob);
        this.clearQueue();
      }
    });
  }
}
